/**
 * The document and slides block editor as data (plan 7.3): every change the
 * native block editor makes, the limits it shows inline, and the inline
 * markup helpers of its keyboard accessory bar (Bold, Italic, Link). Pure
 * functions over `ArtifactDocument`, so the native screen, the web editor's
 * table and diagram forms and the tests share them.
 *
 * The editor works on version 2 text: `toEditableDocument` escapes a
 * version 1 document's literal text once (`escapeInlineText`), so what the
 * user sees while a block is focused is the markup it will be saved with.
 */
import type {
  ArtifactCalloutTone,
  ArtifactDocument,
  ArtifactDocumentBlock,
  ArtifactDocumentSection,
} from '@tabbio/types';
import { escapeInlineText, inlineVisibleLength } from '@tabbio/utils/artifact-inline';

/**
 * The limits the editor checks before saving, mirroring
 * `ARTIFACT_DOCUMENT_LIMITS` in the API (the server checks them again and
 * answers any other limit with its own words).
 */
export const DOCUMENT_EDITOR_LIMITS = {
  sections: 12,
  blocks: 40,
  listItems: 12,
  tableColumns: 6,
  tableRows: 20,
  paragraphChars: 4_000,
  listItemChars: 600,
  quoteChars: 1_200,
  calloutChars: 1_200,
  tableCellChars: 300,
  headingChars: 160,
  titleChars: 160,
  subtitleChars: 240,
  captionChars: 300,
  altChars: 300,
  diagramLabelChars: 80,
} as const;

export type TextBlockType = 'paragraph' | 'bullets' | 'numbered' | 'quote' | 'callout';

export const TEXT_BLOCK_TYPES: readonly TextBlockType[] = ['paragraph', 'bullets', 'numbered', 'quote', 'callout'];

export type LimitIssue = { key: 'sections' | 'blocks' | 'items' | 'rows' | 'columns' | 'characters'; max: number };

/** Where a block is. */
export type BlockPath = { section: number; block: number };

function cloneDocument(document: ArtifactDocument): ArtifactDocument {
  return JSON.parse(JSON.stringify(document)) as ArtifactDocument;
}

function withSections(
  document: ArtifactDocument,
  change: (sections: ArtifactDocumentSection[]) => void,
): ArtifactDocument {
  const next = cloneDocument(document);
  change(next.sections);
  return next;
}

/** The block at `path`, or null. */
export function blockAt(document: ArtifactDocument, path: BlockPath): ArtifactDocumentBlock | null {
  return document.sections[path.section]?.blocks[path.block] ?? null;
}

// ── Version 2 for editing ──────────────────────────────────────────────────

/** A version 1 document as version 2 (literal text escaped once); a version 2 one as it is. */
export function toEditableDocument(document: ArtifactDocument): ArtifactDocument {
  if (document.schemaVersion === 2) return cloneDocument(document);
  const escape = escapeInlineText;
  const next = cloneDocument(document);
  next.schemaVersion = 2;
  for (const section of next.sections) {
    section.blocks = section.blocks.map((block) => {
      switch (block.type) {
        case 'paragraph':
        case 'callout':
        case 'quote':
          return { ...block, text: escape(block.text) };
        case 'bullets':
        case 'numbered':
          return { ...block, items: block.items.map(escape) };
        case 'table':
          return { ...block, columns: block.columns.map(escape), rows: block.rows.map((row) => row.map(escape)) };
        default:
          return block;
      }
    });
  }
  return next;
}

// ── Limits ─────────────────────────────────────────────────────────────────

/** The first limit the document breaks, or null. */
export function documentLimitIssue(document: ArtifactDocument): LimitIssue | null {
  const L = DOCUMENT_EDITOR_LIMITS;
  if (document.sections.length > L.sections) return { key: 'sections', max: L.sections };
  const blocks = document.sections.reduce((sum, section) => sum + section.blocks.length, 0);
  if (blocks > L.blocks) return { key: 'blocks', max: L.blocks };
  for (const section of document.sections) {
    if (section.heading.length > L.headingChars) return { key: 'characters', max: L.headingChars };
    for (const block of section.blocks) {
      switch (block.type) {
        case 'paragraph':
          if (inlineVisibleLength(block.text) > L.paragraphChars) return { key: 'characters', max: L.paragraphChars };
          break;
        case 'quote':
          if (inlineVisibleLength(block.text) > L.quoteChars) return { key: 'characters', max: L.quoteChars };
          break;
        case 'callout':
          if (inlineVisibleLength(block.text) > L.calloutChars) return { key: 'characters', max: L.calloutChars };
          break;
        case 'bullets':
        case 'numbered':
          if (block.items.length > L.listItems) return { key: 'items', max: L.listItems };
          if (block.items.some((item) => inlineVisibleLength(item) > L.listItemChars)) {
            return { key: 'characters', max: L.listItemChars };
          }
          break;
        case 'table':
          if (block.columns.length > L.tableColumns) return { key: 'columns', max: L.tableColumns };
          if (block.rows.length > L.tableRows) return { key: 'rows', max: L.tableRows };
          if ([...block.columns, ...block.rows.flat()].some((cell) => inlineVisibleLength(cell) > L.tableCellChars)) {
            return { key: 'characters', max: L.tableCellChars };
          }
          break;
        default:
          break;
      }
    }
  }
  if (document.title.length > L.titleChars) return { key: 'characters', max: L.titleChars };
  if ((document.subtitle ?? '').length > L.subtitleChars) return { key: 'characters', max: L.subtitleChars };
  return null;
}

/** The copy key of a limit (`chat.artifacts.docEditor.limits.*`). */
export function limitCopyKey(issue: LimitIssue): string {
  return `chat.artifacts.docEditor.limits.${issue.key}`;
}

export function blockCount(document: ArtifactDocument): number {
  return document.sections.reduce((sum, section) => sum + section.blocks.length, 0);
}

export function canAddSection(document: ArtifactDocument): boolean {
  return document.sections.length < DOCUMENT_EDITOR_LIMITS.sections;
}

export function canAddBlock(document: ArtifactDocument): boolean {
  return blockCount(document) < DOCUMENT_EDITOR_LIMITS.blocks;
}

// ── Text ───────────────────────────────────────────────────────────────────

export function setTitle(document: ArtifactDocument, title: string): ArtifactDocument {
  return { ...cloneDocument(document), title };
}

export function setSubtitle(document: ArtifactDocument, subtitle: string): ArtifactDocument {
  const next = cloneDocument(document);
  if (subtitle.trim()) next.subtitle = subtitle;
  else delete next.subtitle;
  return next;
}

export function setHeading(document: ArtifactDocument, section: number, heading: string): ArtifactDocument {
  return withSections(document, (sections) => {
    if (sections[section]) sections[section].heading = heading;
  });
}

/** The text of a paragraph, quote or callout. */
export function setBlockText(document: ArtifactDocument, path: BlockPath, text: string): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block && (block.type === 'paragraph' || block.type === 'quote' || block.type === 'callout')) block.text = text;
  });
}

export function setListItem(document: ArtifactDocument, path: BlockPath, item: number, text: string): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block && (block.type === 'bullets' || block.type === 'numbered') && item < block.items.length) {
      block.items[item] = text;
    }
  });
}

/** A new empty item after `after` (-1 puts it first); refused past the limit. */
export function addListItem(document: ArtifactDocument, path: BlockPath, after: number): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block && (block.type === 'bullets' || block.type === 'numbered') && block.items.length < DOCUMENT_EDITOR_LIMITS.listItems) {
      block.items.splice(after + 1, 0, '');
    }
  });
}

/** Removes an item; the last item of a list removes the list. */
export function removeListItem(document: ArtifactDocument, path: BlockPath, item: number): ArtifactDocument {
  return withSections(document, (sections) => {
    const section = sections[path.section];
    const block = section?.blocks[path.block];
    if (!block || (block.type !== 'bullets' && block.type !== 'numbered')) return;
    if (block.items.length <= 1) section.blocks.splice(path.block, 1);
    else block.items.splice(item, 1);
  });
}

export function setQuoteAttribution(document: ArtifactDocument, path: BlockPath, attribution: string): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block?.type !== 'quote') return;
    if (attribution.trim()) block.attribution = attribution;
    else delete block.attribution;
  });
}

export function setCalloutTone(document: ArtifactDocument, path: BlockPath, tone: ArtifactCalloutTone): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block?.type === 'callout') block.tone = tone;
  });
}

// ── Tables ─────────────────────────────────────────────────────────────────

/** A cell: `row` -1 is the column headings. */
export function setTableCell(
  document: ArtifactDocument,
  path: BlockPath,
  row: number,
  column: number,
  text: string,
): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block?.type !== 'table' || column >= block.columns.length) return;
    if (row < 0) block.columns[column] = text;
    else if (block.rows[row]) block.rows[row][column] = text;
  });
}

export function addTableRow(document: ArtifactDocument, path: BlockPath): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block?.type === 'table' && block.rows.length < DOCUMENT_EDITOR_LIMITS.tableRows) {
      block.rows.push(block.columns.map(() => ''));
    }
  });
}

export function removeTableRow(document: ArtifactDocument, path: BlockPath): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block?.type === 'table' && block.rows.length > 1) block.rows.pop();
  });
}

export function addTableColumn(document: ArtifactDocument, path: BlockPath): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block?.type === 'table' && block.columns.length < DOCUMENT_EDITOR_LIMITS.tableColumns) {
      block.columns.push('');
      for (const row of block.rows) row.push('');
    }
  });
}

export function removeTableColumn(document: ArtifactDocument, path: BlockPath): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block?.type === 'table' && block.columns.length > 1) {
      block.columns.pop();
      for (const row of block.rows) row.splice(block.columns.length);
    }
  });
}

export function setTableCaption(document: ArtifactDocument, path: BlockPath, caption: string): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block?.type !== 'table') return;
    if (caption.trim()) block.caption = caption;
    else delete block.caption;
  });
}

// ── Images and diagrams ────────────────────────────────────────────────────

export function setImageDetails(
  document: ArtifactDocument,
  path: BlockPath,
  details: { alt?: string; caption?: string },
): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block?.type !== 'image') return;
    if (typeof details.alt === 'string') block.alt = details.alt.slice(0, DOCUMENT_EDITOR_LIMITS.altChars);
    if (typeof details.caption === 'string') {
      if (details.caption.trim()) block.caption = details.caption.slice(0, DOCUMENT_EDITOR_LIMITS.captionChars);
      else delete block.caption;
    }
  });
}

/** A diagram's labels (its title and each box); its structure changes through the agent. */
export function setDiagramLabels(
  document: ArtifactDocument,
  path: BlockPath,
  labels: { title?: string; nodes?: Record<string, string> },
): ArtifactDocument {
  return withSections(document, (sections) => {
    const block = sections[path.section]?.blocks[path.block];
    if (block?.type !== 'diagram') return;
    if (typeof labels.title === 'string') {
      if (labels.title.trim()) block.title = labels.title;
      else delete block.title;
    }
    for (const node of block.nodes) {
      const label = labels.nodes?.[node.id];
      if (typeof label === 'string' && label.trim()) {
        node.label = label.slice(0, DOCUMENT_EDITOR_LIMITS.diagramLabelChars);
      }
    }
  });
}

// ── Blocks ─────────────────────────────────────────────────────────────────

/** The words of a text block, one per line for a list. */
function blockWords(block: ArtifactDocumentBlock): string[] {
  switch (block.type) {
    case 'paragraph':
    case 'quote':
    case 'callout':
      return [block.text];
    case 'bullets':
    case 'numbered':
      return block.items;
    default:
      return [];
  }
}

export function isTextBlock(block: ArtifactDocumentBlock | null | undefined): boolean {
  return Boolean(block && (TEXT_BLOCK_TYPES as readonly string[]).includes(block.type));
}

/**
 * Paragraph, bulleted, numbered, quote or callout: a list gets one item per
 * line of the text; a list becomes one text with its items joined.
 */
export function changeBlockType(document: ArtifactDocument, path: BlockPath, type: TextBlockType): ArtifactDocument {
  return withSections(document, (sections) => {
    const section = sections[path.section];
    const block = section?.blocks[path.block];
    if (!block || !isTextBlock(block) || block.type === type) return;
    const words = blockWords(block).filter((word) => word.trim());
    const joined = words.join(' ');
    let next: ArtifactDocumentBlock;
    switch (type) {
      case 'bullets':
      case 'numbered': {
        const items = (words.length > 1 ? words : joined.split(/\n+/))
          .map((item) => item.trim())
          .filter(Boolean)
          .slice(0, DOCUMENT_EDITOR_LIMITS.listItems);
        next = { type, items: items.length ? items : [''] };
        break;
      }
      case 'quote':
        next = { type: 'quote', text: joined };
        break;
      case 'callout':
        next = { type: 'callout', tone: 'info', text: joined };
        break;
      default:
        next = block.type === 'paragraph' ? block : { type: 'paragraph', text: joined };
    }
    section.blocks[path.block] = next;
  });
}

/**
 * Moves a block one place up or down; at the edge of a section it moves to
 * the end of the previous section or the start of the next one. Returns the
 * document and where the block is now (null when it could not move).
 */
export function moveBlock(
  document: ArtifactDocument,
  path: BlockPath,
  direction: 'up' | 'down',
): { document: ArtifactDocument; path: BlockPath | null } {
  const section = document.sections[path.section];
  const block = section?.blocks[path.block];
  if (!block) return { document, path: null };
  const next = cloneDocument(document);
  const blocks = next.sections[path.section].blocks;
  if (direction === 'up') {
    if (path.block > 0) {
      [blocks[path.block - 1], blocks[path.block]] = [blocks[path.block], blocks[path.block - 1]];
      return { document: next, path: { section: path.section, block: path.block - 1 } };
    }
    if (path.section === 0) return { document, path: null };
    const [moved] = blocks.splice(path.block, 1);
    const previous = next.sections[path.section - 1].blocks;
    previous.push(moved);
    return { document: next, path: { section: path.section - 1, block: previous.length - 1 } };
  }
  if (path.block < blocks.length - 1) {
    [blocks[path.block + 1], blocks[path.block]] = [blocks[path.block], blocks[path.block + 1]];
    return { document: next, path: { section: path.section, block: path.block + 1 } };
  }
  if (path.section >= next.sections.length - 1) return { document, path: null };
  const [moved] = blocks.splice(path.block, 1);
  next.sections[path.section + 1].blocks.unshift(moved);
  return { document: next, path: { section: path.section + 1, block: 0 } };
}

export function canMoveBlock(document: ArtifactDocument, path: BlockPath, direction: 'up' | 'down'): boolean {
  return moveBlock(document, path, direction).path !== null;
}

export function deleteBlock(document: ArtifactDocument, path: BlockPath): ArtifactDocument {
  return withSections(document, (sections) => {
    sections[path.section]?.blocks.splice(path.block, 1);
  });
}

/** A new empty paragraph after `after` (-1 puts it first in the section). */
export function addParagraph(document: ArtifactDocument, section: number, after: number): ArtifactDocument {
  if (!canAddBlock(document)) return document;
  return withSections(document, (sections) => {
    sections[section]?.blocks.splice(after + 1, 0, { type: 'paragraph', text: '' });
  });
}

/** A new section (an empty heading and paragraph) after `after`. */
export function addSection(document: ArtifactDocument, after: number): ArtifactDocument {
  if (!canAddSection(document)) return document;
  return withSections(document, (sections) => {
    sections.splice(after + 1, 0, {
      heading: '',
      blocks: canAddBlock(document) ? [{ type: 'paragraph', text: '' }] : [],
    });
  });
}

export function removeSection(document: ArtifactDocument, section: number): ArtifactDocument {
  if (document.sections.length <= 1) return document;
  return withSections(document, (sections) => {
    sections.splice(section, 1);
  });
}

/**
 * The document as it is saved: empty paragraphs, items and blocks the
 * editor keeps while typing are left out.
 */
export function documentForSave(document: ArtifactDocument): ArtifactDocument {
  const next = cloneDocument(document);
  next.schemaVersion = 2;
  for (const section of next.sections) {
    section.blocks = section.blocks.flatMap((block): ArtifactDocumentBlock[] => {
      switch (block.type) {
        case 'paragraph':
        case 'quote':
        case 'callout':
          return block.text.trim() ? [{ ...block, text: block.text.trim() }] : [];
        case 'bullets':
        case 'numbered': {
          const items = block.items.map((item) => item.trim()).filter(Boolean);
          return items.length ? [{ ...block, items }] : [];
        }
        default:
          return [block];
      }
    });
  }
  return next;
}

// ── Inline markup (the accessory bar) ──────────────────────────────────────

export type TextSelection = { start: number; end: number };

/**
 * Bold or italic on the selected markup: wraps it in `**` or `*`, or unwraps
 * it when it already is. With nothing selected, inserts the pair and puts
 * the cursor between. Returns the text and the new selection.
 */
export function toggleInlineMark(
  text: string,
  selection: TextSelection,
  mark: 'bold' | 'italic',
): { text: string; selection: TextSelection } {
  const token = mark === 'bold' ? '**' : '*';
  const start = Math.max(0, Math.min(selection.start, selection.end));
  const end = Math.min(text.length, Math.max(selection.start, selection.end));
  const selected = text.slice(start, end);
  const size = token.length;
  // Already wrapped around the selection: take the markers away.
  const before = text.slice(Math.max(0, start - size), start);
  const after = text.slice(end, end + size);
  const boldAround = mark === 'italic' && text.slice(Math.max(0, start - 2), start) === '**' && text.slice(end, end + 2) === '**';
  if (before === token && after === token && !boldAround) {
    return {
      text: `${text.slice(0, start - size)}${selected}${text.slice(end + size)}`,
      selection: { start: start - size, end: end - size },
    };
  }
  if (selected.startsWith(token) && selected.endsWith(token) && selected.length >= size * 2) {
    const inner = selected.slice(size, selected.length - size);
    return { text: `${text.slice(0, start)}${inner}${text.slice(end)}`, selection: { start, end: start + inner.length } };
  }
  // Keep the spaces at the edges outside the markers, where they can close.
  const lead = /^\s*/.exec(selected)?.[0] ?? '';
  const trail = /\s*$/.exec(selected)?.[0] ?? '';
  const core = selected.slice(lead.length, selected.length - trail.length);
  if (!core) {
    return {
      text: `${text.slice(0, start)}${token}${token}${text.slice(end)}`,
      selection: { start: start + size, end: start + size },
    };
  }
  const wrapped = `${lead}${token}${core}${token}${trail}`;
  return {
    text: `${text.slice(0, start)}${wrapped}${text.slice(end)}`,
    selection: { start: start + lead.length + size, end: start + lead.length + size + core.length },
  };
}

/** A link on the selected words: `[words](href)`; the words default to the address. */
export function insertInlineLink(
  text: string,
  selection: TextSelection,
  href: string,
): { text: string; selection: TextSelection } {
  const start = Math.max(0, Math.min(selection.start, selection.end));
  const end = Math.min(text.length, Math.max(selection.start, selection.end));
  const words = text.slice(start, end).trim() || href;
  const target = href.replace(/[\\()]/g, (char) => `\\${char}`);
  const link = `[${words}](${target})`;
  return {
    text: `${text.slice(0, start)}${link}${text.slice(end)}`,
    selection: { start: start + link.length, end: start + link.length },
  };
}
